import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { ArrowDown, ArrowLeft, ArrowUp, Check, Lock, Search, Tags, X } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import nurseryService from '../../services/nursery';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { errorMessage } from '../../utils/apiResponse';
import { isPbrProtected } from '../../constants/pbr';
import type { NurseryCustomerTier, NurseryStockItem, NurseryTreeType, ProductPayload } from '../../types/nursery';

const inputCls = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

const stockLabel = (si: Pick<NurseryStockItem, 'cultivar_name' | 'rootstock_name'>) =>
  [si.cultivar_name, si.rootstock_name].filter(Boolean).join(' / ');

/**
 * Create or edit an umbrella product: its name and tree type, price and tag
 * colour, and the stock items it is filled from, most preferred first.
 * Membership is chosen by hand — nothing about a rootstock or cultivar puts a
 * stock item in one — from the stock items of the product's tree type only.
 */
function ProductForm() {
  const { t } = useTranslation('nursery', { keyPrefix: 'ProductForm' });
  const navigate = useNavigate();
  const { id } = useParams();
  const productId = id ? Number(id) : null;
  const { hasAccess, loading: licenseLoading } = useModuleLicense('nursery', 'admin');

  const [loading, setLoading] = useState(true);
  const [stockItems, setStockItems] = useState<NurseryStockItem[]>([]);
  const [tiers, setTiers] = useState<NurseryCustomerTier[]>([]);
  const [treeTypes, setTreeTypes] = useState<NurseryTreeType[]>([]);
  const [name, setName] = useState('');
  const [treeTypeId, setTreeTypeId] = useState<number | null>(null);
  // The product's own, kept in case its tree type has since been retired and
  // is no longer in the list.
  const [savedTreeType, setSavedTreeType] = useState<{ id: number; name: string } | null>(null);
  const [hasOrders, setHasOrders] = useState(false);
  const [skuCode, setSkuCode] = useState('');
  const [barcode, setBarcode] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [tagName, setTagName] = useState('');
  const [tagHex, setTagHex] = useState('');
  const [active, setActive] = useState(true);
  const [memberIds, setMemberIds] = useState<number[]>([]);
  const [tierIds, setTierIds] = useState<number[]>([]);
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [stockRes, tierRes, typeRes, productRes, rulesRes] = await Promise.all([
          nurseryService.getStockItems({ kind: 'plant' }),
          nurseryService.getCustomerTiers(),
          nurseryService.getTreeTypes(),
          productId ? nurseryService.getProduct(productId) : Promise.resolve(null),
          productId ? nurseryService.getProductTierRules(productId) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        if (stockRes.success) setStockItems(stockRes.data || []);
        if (tierRes.success) setTiers(tierRes.data || []);
        if (typeRes.success) setTreeTypes(typeRes.data || []);
        if (productRes) {
          if (!productRes.success) { setError(errorMessage(productRes, t('loadProductFailed'))); return; }
          const p = productRes.data;
          setName(p.name);
          setTreeTypeId(p.tree_type_id ?? null);
          if (p.tree_type_id != null) {
            setSavedTreeType({ id: p.tree_type_id, name: p.tree_type_name || p.tree_type_code || t('treeTypeFallback') });
          }
          setHasOrders(!!p.has_orders);
          setSkuCode(p.sku_code ?? '');
          setBarcode(p.barcode ?? '');
          setDescription(p.description ?? '');
          setPrice(p.website_price != null ? String(p.website_price) : '');
          setTagName(p.tag_colour_name ?? '');
          setTagHex(p.tag_colour_hex ?? '');
          setActive(p.active);
          setMemberIds(p.members.map((m) => m.stock_item_id));
        }
        if (rulesRes?.success) setTierIds(rulesRes.data || []);
      } catch (e) {
        if (!cancelled) setError(errorMessage(e, t('loadFailed')));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // t only words a load error; a language change must not reload and overwrite the form.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const byId = useMemo(() => new Map(stockItems.map((si) => [si.id, si])), [stockItems]);
  const members = memberIds.map((mid) => byId.get(mid)).filter((si): si is NurseryStockItem => !!si);

  // A product is one tree type, chosen with its name: a 20cm Dwarf Eureka is
  // only ever filled from 20cm stock. The server refuses anything else.
  const treeTypeOptions = treeTypes.map((tt) => ({ id: tt.id, name: tt.name }));
  if (savedTreeType && !treeTypeOptions.some((tt) => tt.id === savedTreeType.id)) treeTypeOptions.push(savedTreeType);
  const treeTypeLabel = treeTypeOptions.find((tt) => tt.id === treeTypeId)?.name ?? null;
  // Only a product saved before that rule can hold these.
  const wrongType = treeTypeId == null ? [] : members.filter((m) => m.tree_type_id !== treeTypeId);

  // Stock items of the old tree type cannot fill the new one.
  const chooseTreeType = (next: number | null) => {
    setTreeTypeId(next);
    setMemberIds((ids) => ids.filter((mid) => byId.get(mid)?.tree_type_id === next));
    setQuery('');
  };

  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    return stockItems
      .filter((si) => si.active && si.tree_type_id === treeTypeId && !memberIds.includes(si.id))
      .filter((si) => !q || [si.cultivar_name, si.cultivar_trade_name, si.rootstock_name, si.sku_code]
        .some((v) => v && v.toLowerCase().includes(q)))
      .slice(0, 30);
  }, [stockItems, memberIds, query, treeTypeId]);

  // Worth saying out loud before a customer is sold one of these.
  const protectedNames = [...new Set(members.filter((m) => isPbrProtected(m.cultivar_protection_status)).map((m) => m.cultivar_name))];

  const move = (index: number, by: -1 | 1) => {
    setMemberIds((ids) => {
      const next = [...ids];
      const [item] = next.splice(index, 1);
      next.splice(index + by, 0, item);
      return next;
    });
  };

  const save = async () => {
    setError(null);
    if (!name.trim()) { setError(t('errors.name')); return; }
    if (treeTypeId == null) { setError(t('errors.treeType')); return; }
    if (memberIds.length === 0) { setError(t('errors.members')); return; }
    if (wrongType.length > 0) {
      setError(t('errors.wrongType', { treeType: treeTypeLabel }));
      return;
    }
    const priceNum = price.trim() === '' ? null : Number(price);
    if (priceNum != null && (Number.isNaN(priceNum) || priceNum < 0)) { setError(t('errors.price')); return; }

    const payload: ProductPayload = {
      name: name.trim(),
      tree_type_id: treeTypeId,
      sku_code: skuCode.trim() || null,
      barcode: barcode.replace(/\s+/g, '') || null,
      description: description.trim() || null,
      website_price: priceNum,
      tag_colour_name: tagHex ? (tagName.trim() || null) : null,
      tag_colour_hex: tagHex || null,
      active,
      stock_item_ids: memberIds,
    };
    setSaving(true);
    try {
      const res = productId
        ? await nurseryService.updateProduct(productId, payload)
        : await nurseryService.createProduct(payload);
      if (!res.success) { setError(errorMessage(res, t('errors.saveFailed'))); return; }
      const rules = await nurseryService.setProductTierRules(res.data.id, tierIds);
      if (!rules.success) { setError(errorMessage(rules, t('errors.tiersFailed'))); return; }
      navigate('/nursery/products');
    } catch (e) {
      setError(errorMessage(e, t('errors.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  if (licenseLoading || loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="h-8 bg-neutral-100 rounded animate-pulse w-64 mb-4" />
        <div className="h-40 bg-neutral-100 rounded animate-pulse" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-neutral-800 mb-1">{t('noAccess.title')}</h2>
          <p className="text-sm text-neutral-500 mb-4">
            <Trans t={t} i18nKey="noAccess.body" components={{ b: <span className="font-medium" /> }} />
          </p>
          <Button variant="outline" size="sm" onClick={() => navigate('/nursery/products')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> {t('noAccess.back')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={productId ? t('editTitle') : t('newTitle')}
        subtitle={t('subtitle')}
        icon={<Tags className="h-6 w-6 text-white" />}
      />
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        <section className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6 space-y-4">
          <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">{t('customerBuys')}</h3>
          <div>
            <label htmlFor="product-name" className="block text-sm font-medium text-neutral-700 mb-1">
              {t('name')} <span className="text-red-500">*</span>
            </label>
            <input id="product-name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder={t('namePlaceholder')} className={inputCls} />
          </div>
          <div>
            <label htmlFor="product-tree-type" className="block text-sm font-medium text-neutral-700 mb-1">
              {t('treeType')} <span className="text-red-500">*</span>
            </label>
            <select id="product-tree-type" value={treeTypeId ?? ''} disabled={hasOrders}
              onChange={(e) => chooseTreeType(e.target.value ? Number(e.target.value) : null)}
              className={`${inputCls} disabled:bg-neutral-50 disabled:text-neutral-500`}>
              <option value="">{t('chooseTreeType')}</option>
              {treeTypeOptions.map((tt) => <option key={tt.id} value={tt.id}>{tt.name}</option>)}
            </select>
            <p className="text-xs text-neutral-500 mt-1">
              {hasOrders
                ? t('fixedOrdered')
                : members.length > 0 ? t('changingRemoves') : ''}
              {t('anotherProduct')}
            </p>
          </div>
          <p className="text-xs text-neutral-500">
            {t('customerSees')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="product-price" className="block text-sm font-medium text-neutral-700 mb-1">{t('websitePrice')}</label>
              <input id="product-price" type="number" min="0" step="0.01" value={price}
                onChange={(e) => setPrice(e.target.value)} placeholder={t('notOnline')} className={inputCls} />
            </div>
            <div>
              <label htmlFor="product-sku" className="block text-sm font-medium text-neutral-700 mb-1">{t('sku')}</label>
              <input id="product-sku" value={skuCode} onChange={(e) => setSkuCode(e.target.value)} maxLength={50} className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="product-barcode" className="block text-sm font-medium text-neutral-700 mb-1">{t('barcode')}</label>
              <input id="product-barcode" value={barcode} onChange={(e) => setBarcode(e.target.value)} maxLength={30}
                inputMode="numeric" placeholder={t('barcodePlaceholder')} className={`${inputCls} sm:max-w-xs`} />
              <p className="text-xs text-neutral-500 mt-1">
                {t('barcodeHelp')}
              </p>
            </div>
          </div>
          <div>
            <span className="block text-sm font-medium text-neutral-700 mb-1">{t('tagColourTitle')}</span>
            <div className="flex flex-wrap items-center gap-2">
              <input type="color" aria-label={t('tagColour')} value={tagHex || '#1e88e5'}
                onChange={(e) => setTagHex(e.target.value.toLowerCase())}
                className="h-9 w-12 rounded border border-neutral-300 bg-white p-0.5" />
              <input aria-label={t('tagColourName')} value={tagName} onChange={(e) => setTagName(e.target.value)}
                placeholder={t('tagColourPlaceholder')} maxLength={50} className={`${inputCls} flex-1 min-w-0 sm:max-w-xs`} />
              {tagHex ? (
                <button type="button" onClick={() => { setTagHex(''); setTagName(''); }}
                  className="text-xs text-neutral-500 hover:text-neutral-700">{t('noTagColour')}</button>
              ) : (
                <span className="text-xs text-neutral-400">{t('noneSet')}</span>
              )}
            </div>
            <p className="text-xs text-neutral-500 mt-1">{t('tagHelp')}</p>
          </div>
          <div>
            <label htmlFor="product-description" className="block text-sm font-medium text-neutral-700 mb-1">{t('description')}</label>
            <textarea id="product-description" rows={2} value={description} onChange={(e) => setDescription(e.target.value)}
              className={`${inputCls} resize-none`} />
          </div>
          {productId && (
            <label className="inline-flex items-center gap-2 text-sm text-neutral-700">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
              {t('stillSold')}
            </label>
          )}
        </section>

        <section className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6 space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">{t('filledFrom')}</h3>
            <p className="text-xs text-neutral-500 mt-1">
              {t('filledFromHelp')}
            </p>
          </div>

          {members.length === 0 ? (
            <p className="text-sm text-neutral-500">
              {treeTypeId == null ? t('chooseTypeFirst') : t('noneYet')}
            </p>
          ) : (
            <ol className="space-y-2">
              {members.map((m, i) => (
                <li key={m.id} className="flex items-center gap-2 rounded-lg border border-neutral-200 px-3 py-2">
                  <span className="text-xs font-semibold text-neutral-400 w-4">{i + 1}</span>
                  <span className="min-w-0 flex-1 text-sm">
                    <span className="block font-medium text-neutral-800 break-words">{stockLabel(m)}</span>
                    {(m.sku_code || !m.active) && (
                      <span className="block text-xs text-neutral-500 break-words">
                        {[m.sku_code, !m.active ? t('inactiveSkipped') : null].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </span>
                  <button type="button" aria-label={t('moveUp', { item: stockLabel(m) })} disabled={i === 0} onClick={() => move(i, -1)}
                    className="p-1 text-neutral-500 hover:text-neutral-800 disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
                  <button type="button" aria-label={t('moveDown', { item: stockLabel(m) })} disabled={i === members.length - 1} onClick={() => move(i, 1)}
                    className="p-1 text-neutral-500 hover:text-neutral-800 disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
                  <button type="button" aria-label={t('remove', { item: stockLabel(m) })} onClick={() => setMemberIds((ids) => ids.filter((x) => x !== m.id))}
                    className="p-1 text-red-400 hover:text-red-600"><X className="h-4 w-4" /></button>
                </li>
              ))}
            </ol>
          )}

          {wrongType.length > 0 && (
            <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {t('wrongType', {
                count: wrongType.length,
                treeType: treeTypeLabel,
                list: wrongType.map((m) => `${stockLabel(m)} (${m.tree_type_name || m.tree_type_code})`).join(', '),
              })}
            </p>
          )}
          {protectedNames.length > 0 && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              {t('pbr', {
                count: protectedNames.length,
                names: protectedNames.join(', '),
                product: name.trim() || t('thisProduct'),
              })}
            </p>
          )}

          {treeTypeId != null && (
            <div>
              <label htmlFor="product-add" className="block text-sm font-medium text-neutral-700 mb-1">
                {t('addItem', { treeType: treeTypeLabel })}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
                <input id="product-add" value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder={t('searchPlaceholder')} className={`${inputCls} pl-9`} />
              </div>
              <ul className="mt-2 max-h-60 overflow-y-auto divide-y divide-neutral-100 rounded-lg border border-neutral-200">
                {candidates.length === 0 ? (
                  <li className="px-3 py-2 text-sm text-neutral-400">
                    {!stockItems.some((si) => si.active && si.tree_type_id === treeTypeId)
                      ? t('noCandidates', { treeType: treeTypeLabel })
                      : query.trim() ? t('noMatch', { treeType: treeTypeLabel }) : t('allIn', { treeType: treeTypeLabel })}
                  </li>
                ) : candidates.map((si) => (
                  <li key={si.id}>
                    <button type="button" onClick={() => { setMemberIds((ids) => [...ids, si.id]); setQuery(''); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-neutral-50">
                      <span className="font-medium text-neutral-800 break-words">{stockLabel(si)}</span>
                      {si.sku_code && <span className="text-neutral-500"> · {si.sku_code}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        {tiers.length > 0 && (
          <section className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6 space-y-2">
            <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">{t('whoSees')}</h3>
            <p className="text-xs text-neutral-500">
              {tierIds.length === 0
                ? t('everyTier')
                : t('onlyTicked')}{' '}
              {t('ownVisibility')}
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {tiers.map((tier) => (
                <label key={tier.id} className="inline-flex items-center gap-1.5 text-sm text-neutral-700">
                  <input type="checkbox" checked={tierIds.includes(tier.id)}
                    onChange={() => setTierIds((ids) => ids.includes(tier.id) ? ids.filter((x) => x !== tier.id) : [...ids, tier.id])} />
                  {tier.name}
                </label>
              ))}
            </div>
          </section>
        )}

        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div>}

        <div className="flex flex-wrap justify-between gap-2">
          <Button variant="outline" type="button" onClick={() => navigate('/nursery/products')}>
            <ArrowLeft className="h-4 w-4" /> {t('cancel')}
          </Button>
          <Button variant="primary" type="button" onClick={save} disabled={saving}>
            {saving ? t('saving') : productId ? t('save') : t('create')}
            {!saving && <Check className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </>
  );
}

export default ProductForm;
